import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { ApiError } from './api';

/**
 * A failure only deserves a toast when the user cannot see it any other way.
 *
 *  - 401 is handled by the api layer (refresh, then sign-out) — a toast would be
 *    noise on top of a redirect.
 *  - Query failures that have cached data to fall back on are shown inline by the
 *    screen; a toast would double up.
 *  - Mutations always toast, because the user just pressed a button and needs to
 *    know it did not work.
 */
function shouldToast(error: unknown): boolean {
  if (error instanceof ApiError) return error.status !== 401;
  return true;
}

function messageFor(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  if (error instanceof Error) return error.message;
  return 'Something went wrong';
}

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Front-desk data changes constantly but not second-to-second. 30s keeps
      // tab-switching instant without serving stale membership state.
      staleTime: 30_000,
      gcTime: 5 * 60_000,
      // One retry absorbs a flaky proxy; more just delays the error state. Never
      // retry a client error — a 403 will not become a 200.
      retry: (failureCount, error) => {
        if (error instanceof ApiError && error.status >= 400 && error.status < 500) return false;
        return failureCount < 1;
      },
      retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 8000),
      // Screens here are mutation-heavy (billing, attendance). Refetching on every
      // window focus fights with open forms and burns the API for no benefit;
      // screens that genuinely need live data opt in with refetchInterval.
      refetchOnWindowFocus: false,
      refetchOnReconnect: true,
      refetchOnMount: true,
    },
    mutations: {
      retry: false,
    },
  },

  queryCache: new QueryCache({
    onError: (error, query) => {
      // Only surface a background refetch failure if the screen has nothing to show.
      if (query.state.data !== undefined) return;
      if (!shouldToast(error)) return;
      toast.error(messageFor(error));
    },
  }),

  mutationCache: new MutationCache({
    onError: (error, _vars, _ctx, mutation) => {
      // A mutation can opt out by setting meta.silent — useful when the form
      // renders field-level errors itself.
      if (mutation.meta?.silent) return;
      if (!shouldToast(error)) return;
      toast.error(messageFor(error));
    },
    onSuccess: (_data, _vars, _ctx, mutation) => {
      const message = mutation.meta?.successMessage;
      if (typeof message === 'string') toast.success(message);
    },
  }),
});

declare module '@tanstack/react-query' {
  interface Register {
    mutationMeta: {
      /** Suppress the automatic error toast (the form shows field errors instead). */
      silent?: boolean;
      /** Shown as a success toast when the mutation resolves. */
      successMessage?: string;
    };
  }
}

/**
 * Central query key factory. Keeping keys here means an invalidation in one module
 * reliably hits the caches another module populated.
 */
export const qk = {
  dashboard: (branchId: string | null) => ['dashboard', branchId] as const,
  leads: (params?: unknown) => ['leads', params] as const,
  lead: (id: string) => ['lead', id] as const,
  members: (params?: unknown) => ['members', params] as const,
  member: (id: string) => ['member', id] as const,
  attendance: (params?: unknown) => ['attendance', params] as const,
  occupancy: (branchId: string | null) => ['occupancy', branchId] as const,
  slots: (params?: unknown) => ['slots', params] as const,
  invoices: (params?: unknown) => ['invoices', params] as const,
  invoice: (id: string) => ['invoice', id] as const,
  payments: (params?: unknown) => ['payments', params] as const,
  plans: (params?: unknown) => ['plans', params] as const,
  renewals: (params?: unknown) => ['renewals', params] as const,
  pt: (params?: unknown) => ['pt', params] as const,
  sessions: (params?: unknown) => ['sessions', params] as const,
  workouts: (params?: unknown) => ['workouts', params] as const,
  diets: (params?: unknown) => ['diets', params] as const,
  exercises: (params?: unknown) => ['exercises', params] as const,
  feedback: (params?: unknown) => ['feedback', params] as const,
  inventory: (params?: unknown) => ['inventory', params] as const,
  messaging: (params?: unknown) => ['messaging', params] as const,
  reports: (name: string, params?: unknown) => ['reports', name, params] as const,
  settings: (name: string) => ['settings', name] as const,
  me: () => ['me'] as const,
  meHome: () => ['me', 'home'] as const,
} as const;
